import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';
import { IRegionInterviewConfig, ISummaryCalendlyEvent, ISummaryRecordedInterview } from 'types';
import { useDispatch } from 'react-redux';
import { getUniRegionInterviewConfig } from '../../../../store/config/actions';
import { InterviewType, useGetGroupeditems } from './helpers';
import { IEventType } from '../../../../store/event_types/types';

export interface ICancelInterviewModalV2Data {
  clickAction: (data: {
    eventTypeId?: string;
    eventKey?: string;
    eventCancelUrl?: string;
    interviewHash?: string;
  }) => void;
  clickBulkAction: (eventType?: IEventType, regionInterviewConfig?: IRegionInterviewConfig) => void;
  modalTitle: string;
  noticeText: string;
  descriptionText: string;
  confirmationButtonText: string;
  cancelButtonText: string;
  assignedInterviews: Array<ISummaryCalendlyEvent | ISummaryRecordedInterview>;
  uni: string;
  pathway?: string;
}

export interface IProps {
  id?: string;
  modalTitle: string;
  noticeText: string;
  descriptionText: string;
  confirmationButtonText: string;
  cancelButtonText: string;
  uni: string;
  pathway?: string;
  assignedInterviews: Array<ISummaryCalendlyEvent | ISummaryRecordedInterview>;
  onCancelRequest: (data: {
    eventTypeId?: string;
    eventKey?: string;
    eventCancelUrl?: string;
    interviewHash?: string;
  }) => void;
  onClose: () => void;
}

const CancelInterviewV2 = (props: IProps) => {
  const {
    id: _id = 'cancel-interview',
    modalTitle = 'Cancel Interview',
    noticeText = 'If you are cancelling an interview that is not yet booked, the event will be removed, otherwise you will see a followup dialog to Cancel Event.',
    confirmationButtonText,
    cancelButtonText: _cancelButtonText,
    assignedInterviews,
    onCancelRequest,
    onClose,
    uni,
    pathway,
  } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    if (uni.length > 0) dispatch(getUniRegionInterviewConfig(uni, pathway));
  }, [dispatch, uni, pathway]);

  const { groupedItems } = useGetGroupeditems('cancel-interview', assignedInterviews);
  const [selectedInterview, setSelectedInterview] = useState<string>('');
  const [selectedInterviewType, setSelectedInterviewType] = useState<InterviewType>();

  useEffect(() => {
    if (groupedItems?.length && groupedItems[0]?.options.length) {
      const firstItem = groupedItems[0].options[0];
      setSelectedInterview(firstItem.value);
      setSelectedInterviewType(firstItem.type);
    }
  }, [groupedItems]);

  const onChangeInterview = (event: ChangeEvent<HTMLInputElement>): void => {
    const selectedValue = event.currentTarget.value;

    let foundOption;
    groupedItems.some(x => {
      foundOption = x.options.find(y => y.value === selectedValue);
      return foundOption !== undefined;
    });

    if (foundOption) {
      setSelectedInterviewType(foundOption.type);
    } else {
      console.error('No matching option found');
    }

    setSelectedInterview(selectedValue);
  };

  const onCancel = () => {
    if (selectedInterviewType === InterviewType.REC_INTERVIEW) {
      onCancelRequest({ interviewHash: selectedInterview });
    } else {
      const [selectedEventTypeId, selectedInterviewId, eventCancelUrl] = selectedInterview.split('#');
      onCancelRequest({ eventTypeId: selectedEventTypeId, eventKey: selectedInterviewId, eventCancelUrl });
    }
    onClose();
  };

  const renderGroupItems = () => {
    return (
      <RadioGroup name="grouped-radio" defaultValue={selectedInterview}>
        {groupedItems?.map((groupedItem, groupedItemIndex: number) => {
          return (
            <React.Fragment key={`grouped-item-${groupedItemIndex}`}>
              {groupedItem.label && (
                <Flex mb="2" alignItems="center" justifyContent="space-between">
                  <Heading as="h3" fontSize="md">
                    {groupedItem.label}
                  </Heading>
                </Flex>
              )}

              <Stack spacing="0.5" mb="4">
                {groupedItem.options.map(({ id, label, value }, index: number) => (
                  <Radio
                    key={`grouped-item-${id}-${index}`}
                    id={id}
                    value={value}
                    onChange={onChangeInterview}
                    variant="ghost"
                    data-selector={id}
                  >
                    {label}
                  </Radio>
                ))}
              </Stack>
            </React.Fragment>
          );
        })}
      </RadioGroup>
    );
  };

  return (
    <Modal onClose={onClose} isOpen={true} size="md" scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent>
        <ModalHeader display="flex">{modalTitle}</ModalHeader>

        <ModalCloseButton />

        <ModalBody key={`selected-${selectedInterview}`}>
          <Text>{noticeText}</Text>
          {renderGroupItems()}
        </ModalBody>

        <ModalFooter gap="3">
          <Button size="md" onClick={onClose}>
            Cancel
          </Button>

          <Button
            size="md"
            variant="solid"
            onClick={onCancel}
            isDisabled={selectedInterview === '' && assignedInterviews.length !== 1}
          >
            {confirmationButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CancelInterviewV2;
